import { useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, AudioLines, MapPin, Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'

import { gql } from '~/__generated__'
import { TablePageLayout } from '~/layouts/table-page-layout'
import { Button } from '~/ui/button'
import { GraphqlError } from '~/ui/errors'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/ui/table'
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/ui/dialog'
import { Form } from '~/ui/form'
import { toast } from '~/hooks/use-toast'
import { TextField } from '~/fields/text-field'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { useState } from 'react'
import { SelectField } from '~/fields/select-field'
import { useCurrentOrganization } from '~/auth/use-current-organization'

export const locationsQuery = gql(`
  query Locations($first: Int = 20, $after: String, $organizationId: ID) {
    locations(first: $first, after: $after, organizationId: $organizationId) {
      edges {
        node {
          id
          name
          address
          timeZone
          zones {
            id
            name
          }
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`)

export const createLocationMutation = gql(`
  mutation CreateLocation($input: CreateLocationInput!) {
    createLocation(input: $input) {
      location {
        id
        name
        address
        timeZone
        zones {
          id
          name
        }
      }
    }
  }
`)

export const createZoneMutation = gql(`
  mutation CreateZone($input: CreateZoneInput!) {
    createZone(input: $input) {
      zone {
        id
        name
      }
    }
  }
`)

export const updateLocationMutation = gql(`
  mutation UpdateLocation($input: UpdateLocationInput!) {
    updateLocation(input: $input) {
      location {
        id
        name
        address
        timeZone
      }
      errors
    }
  }
`)

const locationFormSchema = z.object({
  name: z.string(),
  address: z.string(),
  timeZone: z.string(),
})

const zoneFormSchema = z.object({
  name: z.string(),
})

type LocationFormValues = z.infer<typeof locationFormSchema>
type ZoneFormValues = z.infer<typeof zoneFormSchema>

const ZonesTable = ({ zones }: any) => {
  const navigate = useNavigate()

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Zone ID</TableHead>
          <TableHead>Zone Name</TableHead>
          <TableHead>Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {zones.map((zone: any) => (
          <TableRow key={zone.id}>
            <TableCell className="text-xs">{zone.id}</TableCell>
            <TableCell>{zone.name}</TableCell>
            <TableCell>
              <Button
                variant="outline"
                size="xs"
                className="cursor-pointer"
                onClick={() => {
                  navigate(`/zones/${zone.id}`)
                }}
              >
                View Zone
                <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export const ZonesScreen = () => {
  const navigate = useNavigate()
  const [newLocationDialogOpen, setNewLocationDialogOpen] = useState(false)
  const [newZoneDialogOpen, setNewZoneDialogOpen] = useState(false)
  const [editLocationDialogOpen, setEditLocationDialogOpen] = useState(false)
  const [editingLocation, setEditingLocation] = useState<any | null>(null)

  const { id: organizationId } = useCurrentOrganization()
  const { data, fetchMore, error, refetch } = useQuery(locationsQuery, {
    variables: { organizationId: organizationId ?? undefined },
    skip: !organizationId,
  })
  const [createLocation] = useSafeMutation(createLocationMutation)
  const [createZone] = useSafeMutation(createZoneMutation)
  const [updateLocation] = useSafeMutation(updateLocationMutation)

  const locationForm = useForm({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      address: '',
      timeZone: '',
    },
  })

  const editLocationForm = useForm({
    resolver: zodResolver(locationFormSchema),
    defaultValues: {
      name: '',
      address: '',
      timeZone: '',
    },
  })

  const zoneForm = useForm({
    resolver: zodResolver(zoneFormSchema),
    defaultValues: {
      name: '',
    },
  })

  const onCreateLocation = async (values: LocationFormValues) => {
    try {
      const response = await createLocation({
        variables: {
          input: {
            name: values.name,
            address: values.address,
            timeZone: values.timeZone,
            organizationId: organizationId ?? undefined,
          },
        },
      })
      if (response.data?.createLocation.location) {
        toast({
          title: 'Location created successfully',
          description: `Location ${response.data.createLocation.location.name} has been created.`,
        })
        locationForm.reset()
        // Optionally, you can refetch the locations or update the cache
      } else {
        toast({
          title: 'Error creating location',
          description: 'Failed to create location. Please try again.',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error creating location:', error)
      toast({
        title: 'Error',
        description: 'Failed to create location. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const onUpdateLocation = async (values: LocationFormValues) => {
    if (!editingLocation) return
    try {
      const response = await updateLocation({
        variables: {
          input: {
            id: editingLocation.id,
            name: values.name,
            address: values.address,
            timeZone: values.timeZone,
          },
        },
      })

      if (response.data?.updateLocation?.errors?.length) {
        toast({
          title: 'Error updating location',
          description: response.data.updateLocation.errors.join(', '),
          variant: 'destructive',
        })
        return
      }

      toast({ title: 'Location updated' })
      setEditLocationDialogOpen(false)
      setEditingLocation(null)
      await refetch()
    } catch (error) {
      console.error('Error updating location:', error)
      toast({
        title: 'Error',
        description: 'Failed to update location. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const onCreateZone = async (values: ZoneFormValues, locationId: string) => {
    try {
      const response = await createZone({
        variables: {
          input: {
            name: values.name,
            locationId,
          },
        },
      })
      if (response.data?.createZone.zone) {
        toast({
          title: 'Zone created successfully',
          description: `Zone ${response.data.createZone.zone.name} has been created.`,
        })
        zoneForm.reset()
        refetch()
        if (response.data.createZone.zone.id) {
          navigate(`/zones/${response.data.createZone.zone.id}`)
        }
      } else {
        toast({
          title: 'Error creating zone',
          description: 'Failed to create zone. Please try again.',
          variant: 'destructive',
        })
      }
    } catch (error) {
      console.error('Error creating zone:', error)
      toast({
        title: 'Error',
        description: 'Failed to create zone. Please try again.',
        variant: 'destructive',
      })
    }
  }

  if (error) {
    return <GraphqlError error={error} />
  }

  const locations = data?.locations.edges.map((edge: any) => edge.node) ?? []
  const pageInfo = data?.locations.pageInfo

  return (
    <TablePageLayout padding={false}>
      <div className="mb-0 flex items-center justify-between border-b border-gray-200 bg-white px-7 py-3">
        <h1 className="text-2xl">Locations / Zones</h1>
        <Dialog open={newLocationDialogOpen} onOpenChange={setNewLocationDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="cursor-pointer">
              <MapPin className="mr-2 h-3.5 w-3.5" />
              <span className="text-sm">Add Location</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>Create New Location</DialogTitle>
            </DialogHeader>
            <Form {...locationForm}>
              <form
                onSubmit={locationForm.handleSubmit(onCreateLocation)}
                className="flex flex-1 flex-col gap-6"
              >
                <TextField
                  control={locationForm.control}
                  name="name"
                  label="Location Name"
                  placeholder="Enter location name"
                  required
                />
                <TextField
                  control={locationForm.control}
                  name="address"
                  label="Address"
                  placeholder="Enter address"
                  required
                />
                <SelectField
                  control={locationForm.control}
                  name="timeZone"
                  label="Time Zone"
                  placeholder="Select time zone"
                  selectEntries={[
                    { value: 'America/New_York', label: 'America/New York' },
                    { value: 'America/Los_Angeles', label: 'America/Los Angeles' },
                    { value: 'Europe/London', label: 'Europe/London' },
                    { value: 'UTC', label: 'UTC' },
                  ]}
                  required
                />
                <DialogFooter>
                  <Button
                    type="button"
                    variant="link"
                    onClick={() => setNewLocationDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button variant="default" type="submit" className="w-full cursor-pointer">
                    <span className="text-sm">Create Location</span>
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
      {locations.length > 0 ? (
        <div className="flex flex-1 flex-col gap-0">
          {locations.map((location: any) => (
            <Card className="w-full gap-0 border-none py-0 shadow-none" key={location.id}>
              <CardHeader className="gap-0 border-b border-gray-200 bg-neutral-100 !p-4">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-1">
                    <CardTitle>{location.name}</CardTitle>
                    <CardDescription>
                      <div className="text-xs text-gray-500">{location.address}</div>
                      <div className="text-xs text-gray-600">Time Zone: {location.timeZone}</div>
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="xs"
                      className="cursor-pointer"
                      onClick={() => {
                        setEditingLocation(location)
                        editLocationForm.reset({
                          name: location.name,
                          address: location.address,
                          timeZone: location.timeZone,
                        })
                        setEditLocationDialogOpen(true)
                      }}
                    >
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit Location
                    </Button>
                    <Dialog open={newZoneDialogOpen} onOpenChange={setNewZoneDialogOpen}>
                      <DialogTrigger asChild>
                        <Button variant="outline" size="xs" className="cursor-pointer">
                          <AudioLines className="mr-2 h-3.5 w-3.5" />
                          Add Zone
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[400px]">
                        <DialogHeader>
                          <DialogTitle>Create New Zone</DialogTitle>
                        </DialogHeader>
                        <Form {...zoneForm}>
                          <form
                            onSubmit={zoneForm.handleSubmit((values) => onCreateZone(values, location.id))}
                            className="flex flex-1 flex-col gap-6"
                          >
                            <TextField
                              control={zoneForm.control}
                              name="name"
                              label="Zone Name"
                              placeholder="Enter zone name"
                              required
                            />
                            <DialogFooter>
                              <Button
                                type="button"
                                variant="link"
                                onClick={() => setNewZoneDialogOpen(false)}
                              >
                                Cancel
                              </Button>
                              <Button variant="default" type="submit" className="w-full cursor-pointer">
                                <span className="text-sm">Create Zone</span>
                              </Button>
                            </DialogFooter>
                          </form>
                        </Form>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <ZonesTable zones={location.zones} />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div>No Locations</div>
      )}

      <Dialog open={editLocationDialogOpen} onOpenChange={setEditLocationDialogOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
          </DialogHeader>
          <Form {...editLocationForm}>
            <form
              onSubmit={editLocationForm.handleSubmit(onUpdateLocation)}
              className="flex flex-1 flex-col gap-6"
            >
              <TextField
                control={editLocationForm.control}
                name="name"
                label="Location Name"
                placeholder="Enter location name"
                required
              />
              <TextField
                control={editLocationForm.control}
                name="address"
                label="Address"
                placeholder="Enter address"
                required
              />
              <SelectField
                control={editLocationForm.control}
                name="timeZone"
                label="Time Zone"
                placeholder="Select time zone"
                selectEntries={[
                  { value: 'America/New_York', label: 'America/New York' },
                  { value: 'America/Los_Angeles', label: 'America/Los Angeles' },
                  { value: 'Europe/London', label: 'Europe/London' },
                  { value: 'UTC', label: 'UTC' },
                ]}
                required
              />
              <DialogFooter>
                <Button type="button" variant="link" onClick={() => setEditLocationDialogOpen(false)}>
                  Cancel
                </Button>
                <Button variant="default" type="submit" className="w-full cursor-pointer">
                  <span className="text-sm">Save</span>
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {pageInfo && pageInfo.hasNextPage && (
        <>
          <hr className="my-4 border-0 border-b" />
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="disabled:bg-gray-100"
              onClick={() => {
                fetchMore({
                  variables: {
                    after: pageInfo.endCursor,
                  },
                })
              }}
              disabled={!pageInfo.hasNextPage}
            >
              Load more
            </Button>
          </div>
        </>
      )}
    </TablePageLayout>
  )
}
